import { EVENTS, TIMEOUTS, SELECTORS } from './shared/constants.js';

/**
 * RealizedPresetRandomizer
 * 
 * Handles randomization of all selections in the realized preset form.
 * Integrates with existing NewRealizedPresetNodeImageEffect for image updates.
 * Uses event-driven coordination with color loading to prevent race conditions.
 */
class RealizedPresetRandomizer {
  constructor() {
    this.skippedNodes = new Set(); // Track nodes that couldn't be randomized
    this.initializeRandomizeButton();
  }

  /**
   * Initialize the randomize button event listener
   */
  initializeRandomizeButton() {
    const randomizeButton = document.querySelector('#randomizePresetButton');
    if (randomizeButton) {
      randomizeButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.randomizeAllSelections();
      });
    }
  }

  /**
   * Randomize all available selections in the form
   */
  async randomizeAllSelections() {
    const nodeContainers = document.querySelectorAll(SELECTORS.PRESET_NODE_CONTAINER);
    
    // Reset skipped nodes tracking
    this.skippedNodes.clear();
    
    // Add visual feedback
    this.showRandomizingState();
    
    // Small delay to show the randomizing state
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // Process all nodes
    const promises = [];
    nodeContainers.forEach(container => {
      const nodeType = container.dataset.nodeType;
      
      // Skip pre-selected inventorius nodes
      if (nodeType === 'inventorius') {
        return;
      }
      
      if (nodeType === 'designation') {
        promises.push(this.randomizeDesignationNode(container));
      } else if (nodeType === 'item_model') {
        this.randomizeItemModelNode(container);
      }
    });
    
    // Wait for all designation nodes to complete (or timeout)
    if (promises.length > 0) {
      await Promise.allSettled(promises);
    }
    
    // Show final state
    this.showFinalRandomizingState();
  }

  /**
   * Randomize a designation node (item_model + color selection)
   * Uses event-driven coordination to wait for color loading completion
   */
  async randomizeDesignationNode(container) {
    const itemModelSelect = container.querySelector(SELECTORS.ITEM_MODEL_SELECT);
    const colorSelect = container.querySelector(SELECTORS.COLOR_SELECT);
    const nodeIndex = container.dataset.nodeIndex;
    
    if (!itemModelSelect || !colorSelect || !nodeIndex) return;
    
    // Get available item model options (excluding prompt)
    const itemModelOptions = Array.from(itemModelSelect.options)
      .filter(option => option.value !== '')
      .map(option => option.value);
    
    if (itemModelOptions.length === 0) return;
    
    try {
      // Randomly select an item model
      const randomItemModelValue = this.getRandomElement(itemModelOptions);
      itemModelSelect.value = randomItemModelValue;
      
      // Trigger change event for item model to start color loading
      itemModelSelect.dispatchEvent(new Event('change', { bubbles: true }));
      
      // Wait for color loading to complete with timeout
      await this.waitForNodeEvent(nodeIndex, EVENTS.COLOR_LOADING_COMPLETED, TIMEOUTS.COLOR_LOADING_MAX_WAIT);
      
      // Now safely select random color
      this.selectRandomColor(colorSelect);
      
    } catch (error) {
      console.warn(`Color loading timeout for node ${nodeIndex}, using fallback`);
      this.handleColorLoadingFallback(container, nodeIndex);
    }
  }

  /**
   * Randomize an item_model node (direct inventorius selection)
   */
  randomizeItemModelNode(container) {
    const inventoriusSelect = container.querySelector('.js-inventorius-select');
    
    if (!inventoriusSelect) return;
    
    // Get available options (excluding prompt)
    const options = Array.from(inventoriusSelect.options)
      .filter(option => option.value !== '')
      .map(option => option.value);
    
    if (options.length === 0) return;
    
    // Randomly select an option
    const randomValue = this.getRandomElement(options);
    inventoriusSelect.value = randomValue;
    
    // Trigger change event
    inventoriusSelect.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Wait for a specific event on a node container
   * @param {string} nodeIndex - The node index to listen on
   * @param {string} eventName - Name of the event to wait for
   * @param {number} timeout - Timeout in milliseconds
   * @returns {Promise} Promise that resolves with event detail or rejects on timeout
   */
  waitForNodeEvent(nodeIndex, eventName, timeout = TIMEOUTS.COLOR_LOADING_MAX_WAIT) {
    return new Promise((resolve, reject) => {
      const container = document.querySelector(`[data-node-index="${nodeIndex}"]`);
      if (!container) {
        reject(new Error(`Container not found for node index: ${nodeIndex}`));
        return;
      }

      const timeoutId = setTimeout(() => {
        container.removeEventListener(eventName, handler);
        reject(new Error(`Timeout waiting for event: ${eventName}`));
      }, timeout);
      
      function handler(event) {
        clearTimeout(timeoutId);
        container.removeEventListener(eventName, handler);
        resolve(event.detail);
      }

      container.addEventListener(eventName, handler, { once: true });
    });
  }

  /**
   * Select a random color from available options
   * @param {HTMLElement} colorSelect - Color select element
   */
  selectRandomColor(colorSelect) {
    const colorOptions = Array.from(colorSelect.options)
      .filter(option => option.value !== '')
      .map(option => option.value);
    
    if (colorOptions.length > 0) {
      const randomColorValue = this.getRandomElement(colorOptions);
      colorSelect.value = randomColorValue;
      colorSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Handle fallback when color loading fails or times out
   * @param {HTMLElement} container - The node container
   * @param {string} nodeIndex - Node index
   */
  handleColorLoadingFallback(container, nodeIndex) {
    this.skippedNodes.add(nodeIndex);
    
    // Strategy 1: Retry with shorter delay
    setTimeout(() => {
      const colorSelect = container.querySelector(SELECTORS.COLOR_SELECT);
      if (colorSelect && colorSelect.options.length > 1) {
        this.selectRandomColor(colorSelect);
        this.skippedNodes.delete(nodeIndex); // Successfully recovered
      } else {
        // Strategy 2: Skip this node and log warning
        console.warn(`Skipping color selection for node ${nodeIndex} - no options available`);
      }
    }, TIMEOUTS.RANDOMIZER_RETRY_DELAY);
  }

  /**
   * Get a random element from an array
   */
  getRandomElement(array) {
    return array[Math.floor(Math.random() * array.length)];
  }

  /**
   * Show randomizing state on the button
   */
  showRandomizingState() {
    const randomizeButton = document.querySelector('#randomizePresetButton');
    if (randomizeButton) {
      randomizeButton.disabled = true;
      randomizeButton.innerHTML = `
        <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        Randomizing...
      `;
    }
  }

  /**
   * Show final randomizing state with appropriate feedback
   */
  showFinalRandomizingState() {
    const randomizeButton = document.querySelector('#randomizePresetButton');
    if (!randomizeButton) return;

    if (this.skippedNodes.size > 0) {
      // Some nodes were skipped - show warning state
      randomizeButton.disabled = false;
      randomizeButton.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-exclamation-triangle me-2 text-warning" viewBox="0 0 16 16">
          <path d="M7.938 2.016A.13.13 0 0 1 8.002 2a.13.13 0 0 1 .063.016.146.146 0 0 1 .054.057l6.857 11.667c.036.06.035.124.002.183a.163.163 0 0 1-.054.06.116.116 0 0 1-.066.017H1.146a.115.115 0 0 1-.066-.017.163.163 0 0 1-.054-.06.176.176 0 0 1 .002-.183L7.884 2.073a.147.147 0 0 1 .054-.057zm1.044-.45a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566z"/>
          <path d="M7.002 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 5.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995z"/>
        </svg>
        Randomize (${this.skippedNodes.size} skipped)
      `;
      
      // Auto-reset to normal state after 3 seconds
      setTimeout(() => this.hideRandomizingState(), 3000);
    } else {
      // All successful - show success briefly then restore
      randomizeButton.disabled = false;
      randomizeButton.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-check-circle me-2 text-success" viewBox="0 0 16 16">
          <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
          <path d="M10.97 4.97a.235.235 0 0 0-.02.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05z"/>
        </svg>
        Randomized!
      `;
      
      // Restore normal state after 1.5 seconds
      setTimeout(() => this.hideRandomizingState(), 1500);
    }
  }

  /**
   * Hide randomizing state and restore button
   */
  hideRandomizingState() {
    const randomizeButton = document.querySelector('#randomizePresetButton');
    if (randomizeButton) {
      randomizeButton.disabled = false;
      // repeat arrows icon; TODO: dry b/n ERB and JS
      randomizeButton.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-arrow-repeat me-2" viewBox="0 0 16 16">
          <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41m-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9"/>
          <path fill-rule="evenodd" d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5 5 0 0 0 8 3M3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9z"/>
        </svg>
        Randomize
      `;
    }
  }
}

export default RealizedPresetRandomizer;
